import { useState, useMemo, useEffect, useRef } from 'react';

import chevronDownIcon from '@shared/assets/images/chevron_down_icon.svg';
import { MobileApprovalList } from '@shared/components/organisms/mobile';
import edit from '@shared/assets/images/edit.svg';

import styles from '@pages/vacation/components/mobile/MobileVacationRequetContent/MobileVacationRequetContent.module.scss';

/**
 * 모바일 휴가 신청 리스트 아이템 (텍스트 영역 전용)
 * - renderText 가독성 개선을 위해 분리
 */
function MobileVacationListItem({ item }) {
  /**
   * 휴가 유형 라벨
   * 예)
   * [연차]
   * [연차 - 종일]
   */
  const vacationTypeLabel = useMemo(() => {
    if (!item?.vacation_type?.name) {
      return '';
    }

    if (!item.cc_type?.name) {
      return `[${item.vacation_type.name}]`;
    }

    return `[${item.vacation_type.name} - ${item.cc_type.name}]`;
  }, [item]);

  return (
    <div className={styles.textFrame}>
      <span className={styles.title}>
        {vacationTypeLabel} {item.reason}
      </span>

      <span className={styles.subtitle}>
        휴가일정 {item.start_date} ~ {item.end_date}
      </span>

      <span className={styles.subtitle}>신청일 {item.date}</span>
    </div>
  );
}

/**
 * 모바일 휴가 신청 내역 컨텐츠
 */
export default function MobileVacationRequetContent({
  isLoading,
  vacationStatus,
  vacationData,
  onItemClick,
  queryYear,
  onChangeYear,
  onClickRequest,
}) {
  const [sortValue, setSortValue] = useState('latest');
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [isYearOpen, setIsYearOpen] = useState(false);
  const sortDropdownRef = useRef(null);
  const yearDropdownRef = useRef(null);

  // 정렬/연도 드롭다운: 외부 터치/클릭 시 닫기 (모바일 포함)
  useEffect(function () {
    function handlePointerOutside(event) {
      const target = event.target;
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(target)) {
        setIsSortOpen(false);
      }
      if (yearDropdownRef.current && !yearDropdownRef.current.contains(target)) {
        setIsYearOpen(false);
      }
    }

    document.addEventListener('mousedown', handlePointerOutside);
    document.addEventListener('touchstart', handlePointerOutside, { passive: true });
    return function () {
      document.removeEventListener('mousedown', handlePointerOutside);
      document.removeEventListener('touchstart', handlePointerOutside);
    };
  }, []);

  const currentYear = new Date().getFullYear();

  const yearOptions = useMemo(() => {
    const years = [];
    for (let i = currentYear; i >= currentYear - 5; i--) {
      years.push(i.toString());
    }
    return years;
  }, [currentYear]);

  /** 정렬 옵션 */
  const sortOptions = [
    { value: 'latest', label: '신청일 최신순' },
    { value: 'oldest', label: '신청일 오래된순' },
  ];

  /** 선택된 정렬 라벨 */
  const selectedSortLabel =
    sortOptions.find((opt) => opt.value === sortValue)?.label ?? '신청일 최신순';

  function handleSortSelect(value) {
    setSortValue(value);
    setIsSortOpen(false);
  }

  /**
   * 필터링 + 정렬
   * - 현재는 필터 조건 없음
   */
  const filteredAndSortedData = useMemo(() => {
    if (!Array.isArray(vacationData) || vacationData.length === 0) {
      return [];
    }

    const filtered = vacationData.filter(() => true);

    return filtered.sort((a, b) => {
      const dateA = a?.date ?? '';
      const dateB = b?.date ?? '';

      if (sortValue === 'latest') {
        return dateB.localeCompare(dateA);
      }

      return dateA.localeCompare(dateB);
    });
  }, [vacationData, sortValue]);

  return (
    <>
      <div className={styles.container}>
        {/* 잔여 휴가 영역 (추후 구현 예정) */}
        <div className={styles.headerTitle}>
          <div className={styles.headerContent}>
            <div className={styles.headerLeft}>
              <div className={styles.headerYear}>{currentYear}년</div>
              <div className={styles.headerContent}> 총 휴가 잔여분</div>
            </div>
            <div className={styles.remainVacation}>
              <span className={styles.headerSpan}>{vacationStatus.remaining.total}일</span>
            </div>
          </div>
        </div>

        {/* 휴가 신청 내역 */}
        <div className={styles.headerContainer}>
          <div className={styles.yearWrapper} ref={yearDropdownRef}>
            <button
              type="button"
              className={styles.yearButton}
              onClick={() => setIsYearOpen((prev) => !prev)}
            >
              <span className={styles.yearLabel}>{queryYear} 휴가 신청내역</span>
              <img
                src={chevronDownIcon}
                alt=""
                width={16}
                height={16}
                className={`${styles.sortChevron} ${isYearOpen ? styles.sortChevronOpen : ''}`}
              />
            </button>

            {isYearOpen && (
              <ul className={styles.sortDropdown}>
                {yearOptions.map((year) => (
                  <li key={year}>
                    <button
                      type="button"
                      className={`${styles.sortOption} ${queryYear === year ? styles.active : ''}`}
                      onClick={() => {
                        onChangeYear(year);
                        setIsYearOpen(false);
                      }}
                    >
                      {year}년
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className={styles.listSection}>
          {/* 정렬 드롭다운 */}
          <div className={styles.sortWrapper} ref={sortDropdownRef}>
            <button
              type="button"
              className={styles.sortButton}
              onClick={() => setIsSortOpen((prev) => !prev)}
            >
              <span className={styles.sortLabel}>{selectedSortLabel}</span>

              <img
                src={chevronDownIcon}
                alt=""
                width={16}
                height={16}
                className={`${styles.sortChevron} ${isSortOpen ? styles.sortChevronOpen : ''}`}
              />
            </button>

            {isSortOpen && (
              <ul className={styles.sortDropdown}>
                {sortOptions.map((option) => (
                  <li key={option.value}>
                    <button
                      type="button"
                      className={`${styles.sortOption} ${
                        sortValue === option.value ? styles.sortOptionActive : ''
                      }`}
                      onClick={() => handleSortSelect(option.value)}
                    >
                      {option.label}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* 리스트 */}
          <div className={styles.listWrapper}>
            {isLoading ? (
              <div className={styles.loadingContainer}>
                <div className={styles.circularProgress} aria-label="휴가 신청내역 로딩 중" />
                <div className={styles.loadingMessage}>휴가 신청 내역 불러오는 중...</div>
              </div>
            ) : (
              <MobileApprovalList
                items={filteredAndSortedData}
                onItemClick={onItemClick}
                emptyMessage="휴가내역이 존재하지 않습니다."
                renderText={(item) => <MobileVacationListItem item={item} />}
              />
            )}
          </div>
          <div className={styles.floatingButton}>
            <button type="button" className={styles.buleCircle} onClick={onClickRequest}>
              <img src={edit} className={styles.edit} />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
